import axios from 'axios';
import { subDays, subMonths } from 'date-fns';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValueFormatterParams } from 'ag-grid-community';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Alert, booleanHeader, Box, Container, CreateHeaders, dateHeader, HeaderSizeEnum, Icon, Tab, Tabs, textHeader } from '@frontend-workspace/react-shared-lib';
import { GetCurrentCompany } from '../../customer-context/customer-context';
import { RelationProductGroupContext } from '../../relation-product-context/relation-product-context';
import { UseDateRangeHook, useMakeRequestHook } from '../../requestUtils';
import PageTitle from '../../page-title/page-title';
import WebkuaTable from '../../webkua-table/webkua-table';
import ProductUsageChart from '../../product-usage-chart/product-usage-chart';

export interface LicenseUsageSummaryUser {
  email: string;
  name: string | null;
  hours: number;
  sessionStarts: number;
  daysUsed: number;
  allSeatsOccupied: number;
  noLicenseAvailable: number;
}

export interface LicenseUsageSummaryRow {
  companyNumber: string;
  companyName: string | null;
  licenseNumber: number;
  productId: string;
  productCode: string;
  productName: string;
  currentSeats: number;
  minRecordedSeats: number | null;
  maxRecordedSeats: number | null;
  isActive: boolean;
  terminationDate: string | null;
  peakOccupiedSeats: number;
  uniqueUsers: number;
  hours: number;
  sessionStarts: number;
  hasRecordedUsage: boolean;
  excludedSeatDays: number;
  userDays: number;
  allSeatsOccupied: number;
  noLicenseAvailable: number;
  users: LicenseUsageSummaryUser[];
}

export interface LicenseUsageFailureRow {
  companyNumber: string;
  email: string;
  productCode: string;
  productName: string;
  licenseNumber: number | null;
  attribution: 'NoMatchingLicense' | 'ProductMismatch' | 'NoLicenseReference' | 'AmbiguousLicense';
  allSeatsOccupied: number;
  noLicenseAvailable: number;
}

interface LicenseUsageReport {
  licenses: LicenseUsageSummaryRow[];
  unassignedFailures: LicenseUsageFailureRow[];
}

export const licenseUsageSummaryExportColumns = [
  'licenseNumber', 'companyNumber', 'companyName', 'productName', 'currentSeats', 'maxRecordedSeats',
  'isActive', 'terminationDate',
  'peakOccupiedSeats', 'uniqueUsers', 'hours', 'userDays', 'allSeatsOccupied', 'excludedSeatDays',
];

export interface UserUsageLicenseRow {
  companyNumber: string;
  companyName: string | null;
  licenseNumber: number;
  productName: string;
  hours: number;
  sessionStarts: number;
  daysUsed: number;
  allSeatsOccupied: number;
  noLicenseAvailable: number;
}

export interface UserUsageSummaryRow extends LicenseUsageSummaryUser {
  licenseCount: number;
  licenses: UserUsageLicenseRow[];
  unassignedFailures: LicenseUsageFailureRow[];
}

export const userUsageSummaryExportColumns = [
  'email', 'name', 'licenseCount', 'hours', 'daysUsed', 'allSeatsOccupied', 'noLicenseAvailable',
];
export const userUsageLicenseExportColumns = [
  'licenseNumber', 'productName', 'companyNumber', 'companyName', 'hours', 'daysUsed',
  'allSeatsOccupied',
];

export interface ProductFailureSummaryRow {
  productCode: string;
  productName: string;
  allSeatsOccupied: number;
  noLicenseAvailable: number;
  failures: LicenseUsageFailureRow[];
}

export const productFailureExportColumns = [
  'productCode', 'productName', 'allSeatsOccupied', 'noLicenseAvailable',
];

export const unassignedFailureExportColumns = [
  'companyNumber', 'email', 'productCode', 'productName', 'licenseNumber', 'attribution',
  'allSeatsOccupied', 'noLicenseAvailable',
];

function getFailurePresentation(failures: LicenseUsageFailureRow[]) {
  const showOccupied = failures.some(failure => failure.allSeatsOccupied > 0);
  const showLicenseNumber = failures.some(failure => failure.licenseNumber !== null);
  const showAttribution = showOccupied || showLicenseNumber ||
    failures.some(failure => failure.attribution !== 'NoLicenseReference');
  return {
    showOccupied, showLicenseNumber, showAttribution,
    tabKey: showAttribution ? 'licenseUsageSummary.rejectionsTab' : 'licenseUsageSummary.noLicenseTab',
    count: failures.reduce((sum, failure) => sum + failure.allSeatsOccupied + failure.noLicenseAvailable, 0),
  };
}

export interface ProductUsageSummaryRow {
  productCode: string;
  productName: string;
  licenseNumber: number | null;
  licenseCount: number;
  currentSeats: number;
  peakOccupiedSeats: number | null;
  minRecordedSeats: number | null;
  maxRecordedSeats: number | null;
  uniqueUsers: number;
  hours: number;
  userDays: number;
  allSeatsOccupied: number;
  noLicenseAvailable: number;
  failures: number;
  excludedSeatDays: number;
  licenses: LicenseUsageSummaryRow[];
  unassignedFailures: LicenseUsageFailureRow[];
}

export const productUsageExportColumns = [
  'productCode', 'productName', 'licenseNumber', 'licenseCount', 'currentSeats', 'maxRecordedSeats', 'peakOccupiedSeats', 'uniqueUsers',
  'hours', 'userDays', 'failures',
];

export function buildProductUsageSummary(
  licenses: LicenseUsageSummaryRow[], unassignedFailures: LicenseUsageFailureRow[],
  catalogue: { id: string; productCode?: string | null; productName?: string | null }[] = [],
): ProductUsageSummaryRow[] {
  const normalizeCode = (rawCode: string | null | undefined) => {
    const code = (rawCode ?? '').trim().toUpperCase();
    return code === 'MANGLENDE PRODUKT KODE' ? '' : code;
  };
  const usableName = (rawName: string | null | undefined) => {
    const name = (rawName ?? '').trim();
    return name.toUpperCase() === 'MANGLENDE PRODUKT NAVN' ? '' : name;
  };
  const catalogueNames = new Map<string, Set<string>>();
  for (const product of catalogue) {
    const name = usableName(product.productName);
    if (!name) continue;
    const code = normalizeCode(product.productCode);
    for (const key of [code ? `code:${code}` : '', product.id ? `id:${product.id}` : '']) {
      if (!key) continue;
      const names = catalogueNames.get(key) ?? new Set<string>();
      names.add(name);
      catalogueNames.set(key, names);
    }
  }
  const products = new Map<string, {
    productCode: string; licenses: LicenseUsageSummaryRow[]; unassignedFailures: LicenseUsageFailureRow[];
  }>();
  const getProduct = (rawCode: string, productId?: string) => {
    const productCode = normalizeCode(rawCode);
    const key = productCode ? `code:${productCode}` : productId ? `id:${productId}` : 'unknown';
    let product = products.get(key);
    if (!product) {
      product = { productCode, licenses: [], unassignedFailures: [] };
      products.set(key, product);
    }
    return product;
  };
  for (const license of licenses)
    getProduct(license.productCode, license.productId).licenses.push(license);
  for (const failure of unassignedFailures)
    getProduct(failure.productCode).unassignedFailures.push(failure);
  return Array.from(products, ([key, product]) => {
    const currentNames = catalogueNames.get(key);
    const names = currentNames?.size === 1 ? currentNames
      : new Set((product.licenses.length > 0 ? product.licenses : product.unassignedFailures)
        .map(row => usableName(row.productName)).filter(Boolean));
    const activeEmails = new Set(product.licenses.flatMap(license =>
      license.users.filter(user => user.daysUsed > 0).map(user => user.email.trim().toLowerCase())));
    const allSeatsOccupied = [...product.licenses, ...product.unassignedFailures]
      .reduce((sum, row) => sum + row.allSeatsOccupied, 0);
    const noLicenseAvailable = [...product.licenses, ...product.unassignedFailures]
      .reduce((sum, row) => sum + row.noLicenseAvailable, 0);
    return {
      ...product,
      productName: names.size === 1 ? Array.from(names)[0] : '',
      licenseNumber: product.licenses.length === 1 ? product.licenses[0].licenseNumber : null,
      licenseCount: product.licenses.length,
      currentSeats: product.licenses.reduce((sum, license) => sum + license.currentSeats, 0),
      peakOccupiedSeats: product.licenses.length === 1 ? product.licenses[0].peakOccupiedSeats : null,
      minRecordedSeats: product.licenses.length === 1 ? product.licenses[0].minRecordedSeats : null,
      maxRecordedSeats: product.licenses.length === 1 ? product.licenses[0].maxRecordedSeats : null,
      uniqueUsers: activeEmails.size,
      hours: product.licenses.reduce((sum, license) => sum + license.hours, 0),
      userDays: product.licenses.reduce((sum, license) => sum + license.userDays, 0),
      allSeatsOccupied,
      noLicenseAvailable,
      failures: allSeatsOccupied + noLicenseAvailable,
      excludedSeatDays: product.licenses.reduce((sum, license) => sum + license.excludedSeatDays, 0),
    };
  }).sort((a, b) => a.productCode.localeCompare(b.productCode) || a.productName.localeCompare(b.productName));
}

export function buildUserUsageSummary(
  licenses: LicenseUsageSummaryRow[], unassignedFailures: LicenseUsageFailureRow[] = [],
): UserUsageSummaryRow[] {
  const users = new Map<string, {
    names: Set<string>; licenses: UserUsageLicenseRow[];
    allSeatsOccupied: number; noLicenseAvailable: number;
    unassignedFailures: LicenseUsageFailureRow[];
  }>();
  const getUser = (recordedEmail: string) => {
    const email = recordedEmail.trim().toLowerCase();
    let summary = users.get(email);
    if (!summary) {
      summary = {
        names: new Set(), licenses: [], allSeatsOccupied: 0, noLicenseAvailable: 0,
        unassignedFailures: [],
      };
      users.set(email, summary);
    }
    return summary;
  };
  for (const license of licenses) {
    for (const user of license.users) {
      const summary = getUser(user.email);
      if (user.name?.trim()) summary.names.add(user.name.trim());
      summary.licenses.push({
        companyNumber: license.companyNumber,
        companyName: license.companyName,
        licenseNumber: license.licenseNumber,
        productName: license.productName,
        hours: user.hours,
        sessionStarts: user.sessionStarts,
        daysUsed: user.daysUsed,
        allSeatsOccupied: user.allSeatsOccupied,
        noLicenseAvailable: user.noLicenseAvailable,
      });
    }
  }
  for (const failure of unassignedFailures) {
    const summary = getUser(failure.email);
    summary.allSeatsOccupied += failure.allSeatsOccupied;
    summary.noLicenseAvailable += failure.noLicenseAvailable;
    summary.unassignedFailures.push(failure);
  }
  return Array.from(users, ([email, summary]) => ({
    email,
    name: summary.names.size === 1 ? Array.from(summary.names)[0] : null,
    licenseCount: summary.licenses.filter(license => license.daysUsed > 0).length,
    hours: summary.licenses.reduce((total, license) => total + license.hours, 0),
    sessionStarts: summary.licenses.reduce((total, license) => total + license.sessionStarts, 0),
    daysUsed: summary.licenses.reduce((total, license) => total + license.daysUsed, 0),
    allSeatsOccupied: summary.licenses.reduce((total, license) => total + license.allSeatsOccupied, summary.allSeatsOccupied),
    noLicenseAvailable: summary.licenses.reduce((total, license) => total + license.noLicenseAvailable, summary.noLicenseAvailable),
    licenses: summary.licenses,
    unassignedFailures: summary.unassignedFailures,
  })).sort((a, b) => a.email.localeCompare(b.email));
}

export function buildProductFailureSummary(failures: LicenseUsageFailureRow[]): ProductFailureSummaryRow[] {
  const products = new Map<string, LicenseUsageFailureRow[]>();
  for (const failure of failures) {
    const code = failure.productCode.trim().toUpperCase();
    const entries = products.get(code);
    if (entries) entries.push(failure);
    else products.set(code, [failure]);
  }
  return Array.from(products, ([productCode, entries]) => {
    const names = new Set(entries.map(entry => entry.productName.trim()).filter(Boolean));
    return {
      productCode,
      productName: names.size === 1 ? Array.from(names)[0] : '',
      allSeatsOccupied: entries.reduce((total, entry) => total + entry.allSeatsOccupied, 0),
      noLicenseAvailable: entries.reduce((total, entry) => total + entry.noLicenseAvailable, 0),
      failures: entries,
    };
  }).sort((a, b) => a.productCode.localeCompare(b.productCode));
}

export function formatRecordedSeatRange(min: number | null, max: number | null): string | null {
  if (min === null || max === null) return null;
  return min === max ? String(max) : `${min}-${max}`;
}

function useLicenseUsageSummary() {
  const company = GetCurrentCompany();
  const products = useContext(RelationProductGroupContext);
  const makeRequest = useMakeRequestHook();
  const now = new Date();
  const today = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const { startDate, endDate, onDateChange } = UseDateRangeHook(
    subMonths(today, 1), subDays(today, 1));
  const [forGroup, setForGroup] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [data, setData] = useState<LicenseUsageSummaryRow[]>([]);
  const [unassignedFailures, setUnassignedFailures] = useState<LicenseUsageFailureRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  const query = new URLSearchParams();
  selectedProductIds.forEach(id => query.append('values', id));
  const companyPath = company ? `/${encodeURIComponent(company.number)}/forGroup/${forGroup}` : '';
  const url = `reports/license-usage-summary/details/${startDate}/${endDate}${companyPath}?${query}`;

  useEffect(() => {
    const invalidateRequests = () => { requestId.current++; };
    invalidateRequests();
    setData([]);
    setUnassignedFailures([]);
    setError(null);
    setIsLoading(false);
    setHasLoaded(false);
    return invalidateRequests;
  }, [url]);

  const getData = async () => {
    const id = ++requestId.current;
    setData([]);
    setUnassignedFailures([]);
    setError(null);
    setHasLoaded(false);
    setIsLoading(true);
    try {
      const result: LicenseUsageReport = await makeRequest(url);
      if (id === requestId.current) {
        setData(result.licenses);
        setUnassignedFailures(result.unassignedFailures);
        setHasLoaded(true);
      }
    } catch (failure) {
      if (id === requestId.current) {
        if (axios.isAxiosError(failure)) {
          const problem: unknown = failure.response?.data;
          const title: unknown = typeof problem === 'object' && problem !== null
            ? Reflect.get(problem, 'title') : undefined;
          setError(typeof title === 'string' ? title : failure.message);
        } else {
          setError(failure instanceof Error ? failure.message : String(failure));
        }
      }
    } finally {
      if (id === requestId.current) setIsLoading(false);
    }
  };

  const dateRange: DateRange<Date> = [
    new Date(`${startDate}T00:00:00`), new Date(`${endDate}T00:00:00`),
  ];
  return {
    data, unassignedFailures, error, isLoading, hasLoaded, getData, onDateChange,
    dateRange,
    forGroup, onForGroupToggle: () => setForGroup(value => !value),
    productFilter: { products, selectedProductGroupIds: selectedProductIds, setSelectedProductGroupIds: setSelectedProductIds },
  };
}

function useLicenseUsageHeaders() {
  const { t, i18n } = useTranslation();
  const hoursFormatter = (params: ValueFormatterParams) => typeof params.value === 'number'
    ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(params.value) : '';
  const seatsRenderer = (params: ValueFormatterParams) => {
    const row: LicenseUsageSummaryRow | undefined = params.data;
    return row ? formatRecordedSeatRange(row.minRecordedSeats, row.maxRecordedSeats)
      ?? t('licenseUsageSummary.unknown') : t('licenseUsageSummary.unknown');
  };
  const numeric = { cellStyle: { justifyContent: 'right' }, headerClass: 'ag-right-aligned-header' };
  return CreateHeaders([
    textHeader('licenseNumber', t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM, { renderer: 'agGroupCellRenderer' }),
    textHeader('companyNumber', t('standard.companyNumber'), HeaderSizeEnum.MEDIUM),
    textHeader('companyName', t('standard.companyName'), HeaderSizeEnum.EXTRA_LARGE),
    textHeader('productName', t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE),
    textHeader('currentSeats', t('licenseUsageSummary.currentSeats'), HeaderSizeEnum.MEDIUM, { ...numeric, sort: 'desc' }),
    textHeader('maxRecordedSeats', t('licenseUsageSummary.recordedSeats'), HeaderSizeEnum.MEDIUM, {
      ...numeric, renderer: seatsRenderer, getQuickFilterText: params => seatsRenderer(params) ?? '',
    }),
    booleanHeader('isActive', t('standard.active')),
    dateHeader('terminationDate', t('licenseUsageSummary.terminationDate'), { hide: true }),
    textHeader('peakOccupiedSeats', t('licenseUsageSummary.peakOccupiedSeats'), HeaderSizeEnum.MEDIUM, numeric),
    textHeader('uniqueUsers', t('licenseUsageSummary.uniqueUsers'), HeaderSizeEnum.MEDIUM, numeric),
    textHeader('hours', t('licenseUsageSummary.hours'), HeaderSizeEnum.MEDIUM, { ...numeric, valueFormatter: hoursFormatter }),
    textHeader('userDays', t('licenseUsageSummary.userDays'), HeaderSizeEnum.MEDIUM, numeric),
    textHeader('allSeatsOccupied', t('licenseUsageSummary.failures'), HeaderSizeEnum.MEDIUM, numeric),
    textHeader('excludedSeatDays', t('licenseUsageSummary.excludedSeatDays'), HeaderSizeEnum.MEDIUM, numeric),
  ]);
}

export function LicenseUsageSummary() {
  const { t, i18n } = useTranslation();
  const report = useLicenseUsageSummary();
  const licenseHeaders = useLicenseUsageHeaders();
  const [showInactive, setShowInactive] = useState(false);
  const [view, setView] = useState<'licenses' | 'users' | 'products'>('licenses');
  const [showGraph, setShowGraph] = useState(false);
  const [hasOpenedGraph, setHasOpenedGraph] = useState(false);
  const isUserView = view === 'users';
  const isProductView = view === 'products';
  const visibleRows = report.data.filter(row => showInactive || row.isActive);
  const userRows = buildUserUsageSummary(visibleRows, report.unassignedFailures);
  const productRows = isProductView
    ? buildProductUsageSummary(visibleRows, report.unassignedFailures, report.productFilter.products) : [];
  const displayedRows = isUserView ? userRows : isProductView ? productRows : visibleRows;
  const incompleteLicenses = visibleRows.filter(row => row.excludedSeatDays > 0);
  const hoursFormatter = (params: ValueFormatterParams) => typeof params.value === 'number'
    ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(params.value) : '';
  const numeric = { cellStyle: { justifyContent: 'right' }, headerClass: 'ag-right-aligned-header' };
  const identityFormatter = (params: ValueFormatterParams) =>
    params.value || t('licenseUsageSummary.unknown');
  const productSeatsRenderer = (params: ValueFormatterParams) => {
    const row: ProductUsageSummaryRow | undefined = params.data;
    return row && row.licenseCount > 1 ? t('licenseUsageSummary.seeLicenseDetails')
      : row ? formatRecordedSeatRange(row.minRecordedSeats, row.maxRecordedSeats)
        ?? t('licenseUsageSummary.unknown') : t('licenseUsageSummary.unknown');
  };
  const productSingleValueRenderer = (params: ValueFormatterParams) => {
    const row: ProductUsageSummaryRow | undefined = params.data;
    return typeof params.value === 'number' ? params.value
      : t(row && row.licenseCount > 1 ? 'licenseUsageSummary.seeLicenseDetails' : 'licenseUsageSummary.unknown');
  };

  return <Container className="container-fluid pt-4 px-0" maxWidth={false} sx={{ paddingBottom: '50px' }}>
    {!showGraph && <PageTitle title={t(isUserView ? 'licenseUsageSummary.usersTitle'
      : isProductView ? 'licenseUsageSummary.productsTitle' : 'licenseUsageSummary.title')}
      subtitle={`(${t('standard.reports')})`} />}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Tabs value={showGraph ? 'graph' : view} aria-label={t('licenseUsageSummary.perspective') ?? ''}>
      <Tab id="license-usage-licenses-tab" value="licenses" aria-controls="license-usage-table"
        label={t('licenseUsageSummary.licensesTab')} onClick={() => { setView('licenses'); setShowGraph(false); }} />
      <Tab id="license-usage-users-tab" value="users" aria-controls="license-usage-table"
        label={t('licenseUsageSummary.usersTab')} onClick={() => { setView('users'); setShowGraph(false); }} />
      <Tab id="license-usage-products-tab" value="products" aria-controls="license-usage-table"
        label={t('licenseUsageSummary.productsTab')} onClick={() => { setView('products'); setShowGraph(false); }} />
      <Tab id="license-usage-graph-tab" value="graph" aria-controls="license-usage-graph"
        label={t('licenseUsageSummary.graphTab')} onClick={() => { setHasOpenedGraph(true); setShowGraph(true); }} />
    </Tabs>
    {report.hasLoaded && !showGraph && <Tooltip arrow describeChild placement="bottom-end"
      slotProps={{ tooltip: { sx: { maxWidth: 640, fontSize: '0.875rem' } } }}
      title={<div>
        <p>{t('licenseUsageSummary.daysAndFailuresInfo')}</p>
        {isProductView && <p>{t('licenseUsageSummary.productUsageInfo')}</p>}
        {isProductView && productRows.some(row => row.licenseCount > 1) &&
          <p>{t('licenseUsageSummary.multipleLicensePeaksInfo')}</p>}
        <p>{t('licenseUsageSummary.historicalModuleUsageInfo')}</p>
        {incompleteLicenses.length > 0 && <Alert severity="warning">
          {t('licenseUsageSummary.incompleteSeatCoverage', {
            licenses: incompleteLicenses.map(row => row.licenseNumber).join(', '),
          })}
        </Alert>}
      </div>}>
      <IconButton size="small" color={incompleteLicenses.length > 0 ? 'warning' : 'info'}
        aria-label={t(incompleteLicenses.length > 0
          ? 'licenseUsageSummary.reportInfoWithWarnings' : 'licenseUsageSummary.reportInfo') ?? ''}>
        <Icon fontSize="small">info_outline</Icon>
      </IconButton>
    </Tooltip>}
    </Box>
    {hasOpenedGraph && <div role="tabpanel" id="license-usage-graph" aria-labelledby="license-usage-graph-tab"
      hidden={!showGraph}>
      <ProductUsageChart />
    </div>}
    <div hidden={showGraph}>
    {report.error && <Alert severity="error" className="my-3">{report.error}</Alert>}
    {report.hasLoaded && displayedRows.length === 0 &&
      <Alert severity="info" className="my-3">
        {t(isUserView ? 'licenseUsageSummary.noRecordedUsers' : 'licenseUsageSummary.empty')}
      </Alert>}
    <div role="tabpanel" id="license-usage-table" aria-labelledby={`license-usage-${view}-tab`}>
    <WebkuaTable
      key={view}
      data={displayedRows}
      headers={isUserView ? CreateHeaders([
        textHeader('email', t('standard.email'), HeaderSizeEnum.EXTRA_LARGE, {
          renderer: 'agGroupCellRenderer', valueFormatter: identityFormatter,
        }),
        textHeader('name', t('standard.name'), HeaderSizeEnum.EXTRA_LARGE),
        textHeader('licenseCount', t('licenseUsageSummary.licensesUsed'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('hours', t('licenseUsageSummary.hours'), HeaderSizeEnum.MEDIUM, { ...numeric, valueFormatter: hoursFormatter }),
        textHeader('daysUsed', t('licenseUsageSummary.userDays'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('allSeatsOccupied', t('licenseUsageSummary.allSeatsOccupied'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('noLicenseAvailable', t('licenseUsageSummary.noLicenseAvailable'), HeaderSizeEnum.MEDIUM, numeric),
      ]) : isProductView ? CreateHeaders([
        textHeader('productCode', t('licenseUsageSummary.productCode'), HeaderSizeEnum.MEDIUM, {
          renderer: 'agGroupCellRenderer', valueFormatter: identityFormatter,
        }),
        textHeader('productName', t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE, { valueFormatter: identityFormatter }),
        textHeader('licenseNumber', t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM, {
          ...numeric, renderer: productSingleValueRenderer,
          getQuickFilterText: params => String(productSingleValueRenderer(params) ?? ''),
        }),
        textHeader('licenseCount', t('licenseUsageSummary.licensesTab'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('currentSeats', t('licenseUsageSummary.currentSeats'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('maxRecordedSeats', t('licenseUsageSummary.recordedSeats'), HeaderSizeEnum.MEDIUM, {
          ...numeric, renderer: productSeatsRenderer,
          getQuickFilterText: params => productSeatsRenderer(params) ?? '',
        }),
        textHeader('peakOccupiedSeats', t('licenseUsageSummary.peakOccupiedSeats'), HeaderSizeEnum.MEDIUM, {
          ...numeric, renderer: productSingleValueRenderer,
          getQuickFilterText: params => String(productSingleValueRenderer(params) ?? ''),
        }),
        textHeader('uniqueUsers', t('licenseUsageSummary.uniqueUsers'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('hours', t('licenseUsageSummary.hours'), HeaderSizeEnum.MEDIUM, { ...numeric, valueFormatter: hoursFormatter }),
        textHeader('userDays', t('licenseUsageSummary.userDays'), HeaderSizeEnum.MEDIUM, numeric),
        textHeader('failures', t('licenseUsageSummary.failures'), HeaderSizeEnum.MEDIUM, numeric),
      ]) : licenseHeaders}
      expandComponent={isUserView ? UserUsageSummaryLicenses : isProductView ? ProductUsageDetails : LicenseUsageSummaryUsers}
      exportColumnKeys={isUserView ? userUsageSummaryExportColumns
        : isProductView ? productUsageExportColumns : licenseUsageSummaryExportColumns}
      exportVisibleColumnsOnly
      showNoDataAlert={!report.hasLoaded && !report.isLoading && !report.error}
      dateRange={report.dateRange}
      onSetDateRange={report.onDateChange}
      productsFilter={report.productFilter}
      tableFilters={[{
        label: t('standard.showForGroup'), checked: report.forGroup, onChange: report.onForGroupToggle,
      }, {
        label: t('licenseUsageSummary.showInactive'), checked: showInactive,
        onChange: () => setShowInactive(value => !value),
      }]}
      onRefresh={() => { void report.getData(); }}
      isLoading={report.isLoading}
    />
    {report.hasLoaded && !isProductView && report.unassignedFailures.length > 0 && <Box className="mt-3">
      <h2>{t('licenseUsageSummary.unassignedFailures')}</h2>
      <Alert severity="warning">{t('licenseUsageSummary.unassignedFailuresInfo')}</Alert>
      <ProductFailures failures={report.unassignedFailures} />
    </Box>}
    </div>
    </div>
  </Container>;
}

export function LicenseUsageSummaryUsers({ data }: { data: LicenseUsageSummaryRow }) {
  const { t, i18n } = useTranslation();
  return <Box className="p-4">
    {data.users.length === 0
      ? <Alert severity="info">{t('licenseUsageSummary.noUsers')}</Alert>
      : <WebkuaTable
        data={data.users}
        headers={CreateHeaders([
          textHeader('email', t('standard.email'), HeaderSizeEnum.EXTRA_LARGE, {
            valueFormatter: (params: ValueFormatterParams) => params.value || t('licenseUsageSummary.unknown'),
          }),
          textHeader('name', t('standard.name'), HeaderSizeEnum.EXTRA_LARGE),
          textHeader('daysUsed', t('licenseUsageSummary.daysUsed'), HeaderSizeEnum.MEDIUM),
          textHeader('allSeatsOccupied', t('licenseUsageSummary.failures'), HeaderSizeEnum.MEDIUM),
          textHeader('hours', t('licenseUsageSummary.hours'), HeaderSizeEnum.MEDIUM, {
            valueFormatter: (params: ValueFormatterParams) => typeof params.value === 'number'
              ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(params.value) : '',
          }),
        ])}
        exportColumnKeys={['email', 'name', 'daysUsed', 'allSeatsOccupied', 'hours']}
        exportVisibleColumnsOnly
      />}
  </Box>;
}

export function UserUsageSummaryLicenses({ data }: { data: UserUsageSummaryRow }) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<'usage' | 'failures'>(() =>
    data.daysUsed > 0 || data.unassignedFailures.length === 0 ? 'usage' : 'failures');
  const id = useId();
  const failures = getFailurePresentation(data.unassignedFailures);
  return <Box className="p-4">
    <Tabs value={view} aria-label={t('licenseUsageSummary.userDetails') ?? ''}>
      <Tab id={`${id}-usage-tab`} value="usage" aria-controls={`${id}-panel`}
        label={t('licenseUsageSummary.usageTab')} onClick={() => setView('usage')} />
      <Tab id={`${id}-failures-tab`} value="failures" aria-controls={`${id}-panel`}
        label={t(failures.tabKey, { count: failures.count })} onClick={() => setView('failures')} />
    </Tabs>
    <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${view}-tab`}>
    {view === 'usage' && (data.licenses.length === 0
      ? <Alert severity="info">{t('licenseUsageSummary.noUserLicenses')}</Alert>
      : <WebkuaTable
      data={data.licenses}
      headers={CreateHeaders([
        textHeader('licenseNumber', t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM),
        textHeader('productName', t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE),
        textHeader('companyNumber', t('standard.companyNumber'), HeaderSizeEnum.MEDIUM),
        textHeader('companyName', t('standard.companyName'), HeaderSizeEnum.EXTRA_LARGE),
        textHeader('daysUsed', t('licenseUsageSummary.daysUsed'), HeaderSizeEnum.MEDIUM),
        textHeader('allSeatsOccupied', t('licenseUsageSummary.failures'), HeaderSizeEnum.MEDIUM),
        textHeader('hours', t('licenseUsageSummary.userHours'), HeaderSizeEnum.MEDIUM, {
          valueFormatter: (params: ValueFormatterParams) => typeof params.value === 'number'
            ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(params.value) : '',
        }),
      ])}
      exportColumnKeys={userUsageLicenseExportColumns}
      exportVisibleColumnsOnly
    />)}
    {view === 'failures' && (data.unassignedFailures.length === 0
      ? <Alert severity="info">{t('licenseUsageSummary.noUnassignedUserFailures')}</Alert>
      : <>
        <Alert severity="info">{t('licenseUsageSummary.userUnassignedFailuresInfo')}</Alert>
        <ProductFailures failures={data.unassignedFailures} />
      </>)}
    </div>
  </Box>;
}

function ProductFailures({ failures }: { failures: LicenseUsageFailureRow[] }) {
  const { t } = useTranslation();
  const presentation = getFailurePresentation(failures);
  const identityFormatter = (params: ValueFormatterParams) =>
    params.value || t('licenseUsageSummary.unknown');
  const numeric = { cellStyle: { justifyContent: 'right' }, headerClass: 'ag-right-aligned-header' };
  return <WebkuaTable
    data={buildProductFailureSummary(failures)}
    headers={CreateHeaders([
      textHeader('productCode', t('licenseUsageSummary.productCode'), HeaderSizeEnum.MEDIUM, {
        renderer: 'agGroupCellRenderer', valueFormatter: identityFormatter,
      }),
      textHeader('productName', t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE, { valueFormatter: identityFormatter }),
      ...(presentation.showOccupied ? [
        textHeader('allSeatsOccupied', t('licenseUsageSummary.allSeatsOccupied'), HeaderSizeEnum.MEDIUM, numeric),
      ] : []),
      textHeader('noLicenseAvailable', t('licenseUsageSummary.noLicenseAvailable'), HeaderSizeEnum.MEDIUM, numeric),
    ])}
    expandComponent={ProductFailureDetails}
    exportColumnKeys={productFailureExportColumns.filter(field => field !== 'allSeatsOccupied' || presentation.showOccupied)}
    exportVisibleColumnsOnly
  />;
}

export function ProductUsageDetails({ data }: { data: ProductUsageSummaryRow }) {
  const { t } = useTranslation();
  const headers = useLicenseUsageHeaders();
  const [view, setView] = useState<'licenses' | 'failures'>(() =>
    data.licenses.length > 0 ? 'licenses' : 'failures');
  const id = useId();
  const failures = getFailurePresentation(data.unassignedFailures);
  return <Box className="p-4">
    <Tabs value={view} aria-label={t('licenseUsageSummary.productDetails') ?? ''}>
      <Tab id={`${id}-licenses-tab`} value="licenses" aria-controls={`${id}-panel`}
        label={t(data.licenses.length === 1 ? 'licenseUsageSummary.usersTab' : 'licenseUsageSummary.licensesTab')}
        onClick={() => setView('licenses')} />
      <Tab id={`${id}-failures-tab`} value="failures" aria-controls={`${id}-panel`}
        label={t(failures.tabKey, { count: failures.count })} onClick={() => setView('failures')} />
    </Tabs>
    <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${view}-tab`}>
      {view === 'licenses' && (data.licenses.length === 0
        ? <Alert severity="info">{t('licenseUsageSummary.noProductLicenses')}</Alert>
        : data.licenses.length === 1 ? <LicenseUsageSummaryUsers data={data.licenses[0]} />
        : <WebkuaTable
          data={data.licenses}
          headers={headers}
          expandComponent={LicenseUsageSummaryUsers}
          exportColumnKeys={licenseUsageSummaryExportColumns}
          exportVisibleColumnsOnly
        />)}
      {view === 'failures' && (data.unassignedFailures.length === 0
        ? <Alert severity="info">{t('licenseUsageSummary.noUnassignedProductFailures')}</Alert>
        : <>
          <Alert severity="info">{t('licenseUsageSummary.productUnassignedFailuresInfo')}</Alert>
          <ProductFailureDetails data={{ failures: data.unassignedFailures }} />
        </>)}
    </div>
  </Box>;
}

export function ProductFailureDetails({ data }: { data: { failures: LicenseUsageFailureRow[] } }) {
  const { t } = useTranslation();
  const presentation = getFailurePresentation(data.failures);
  const identityFormatter = (params: ValueFormatterParams) =>
    params.value || t('licenseUsageSummary.unknown');
  return <Box className="p-4">
    <WebkuaTable
      data={data.failures}
      headers={CreateHeaders([
        textHeader('companyNumber', t('standard.companyNumber'), HeaderSizeEnum.MEDIUM, { valueFormatter: identityFormatter }),
        textHeader('email', t('standard.email'), HeaderSizeEnum.EXTRA_LARGE, { valueFormatter: identityFormatter }),
        textHeader('productCode', t('licenseUsageSummary.productCode'), HeaderSizeEnum.MEDIUM, { valueFormatter: identityFormatter }),
        textHeader('productName', t('standard.productName'), HeaderSizeEnum.EXTRA_LARGE, { valueFormatter: identityFormatter }),
        ...(presentation.showLicenseNumber ? [
          textHeader('licenseNumber', t('standard.licenseNumber'), HeaderSizeEnum.MEDIUM, { valueFormatter: identityFormatter }),
        ] : []),
        ...(presentation.showAttribution ? [
          textHeader('attribution', t('licenseUsageSummary.attribution'), HeaderSizeEnum.EXTRA_LARGE, {
            renderer: (params: ValueFormatterParams) => t(`licenseUsageSummary.${params.value}`),
          }),
        ] : []),
        ...(presentation.showOccupied ? [
          textHeader('allSeatsOccupied', t('licenseUsageSummary.allSeatsOccupied'), HeaderSizeEnum.MEDIUM),
        ] : []),
        textHeader('noLicenseAvailable', t('licenseUsageSummary.noLicenseAvailable'), HeaderSizeEnum.MEDIUM),
      ])}
      exportColumnKeys={unassignedFailureExportColumns.filter(field =>
        (field !== 'licenseNumber' || presentation.showLicenseNumber) &&
        (field !== 'attribution' || presentation.showAttribution) &&
        (field !== 'allSeatsOccupied' || presentation.showOccupied))}
      exportVisibleColumnsOnly
    />
  </Box>;
}
